import {FormControl, Validators} from '@angular/forms';
import {CoreAppFormGroup} from '@core/form/form.group';
import {ApiExamPlanInterface, ExamAssignInterface} from '@component/exam/exam.service';
import {AppDateFormControl} from '@core/form/form-date.control';
import {HttpMethodsEnum} from '@core/http/methods.enum';
import * as moment from 'moment';
import {HttpValidationErrorResponseContextInterface} from '@core/interfaces/http-error-response.interface';

export enum AddExamFormFields {
    Title = 'title',
    UserIds = 'userIds',
    Book = 'book',
    Exam = 'exam',
    StartDate = 'startDate',
    StartHour = 'startHour',
    StartMinute = 'startMinute',
    EndDate = 'endDate',
    EndHour = 'endHour',
    EndMinute = 'endMinute',
}

export class AppExamAssignDialogForm extends CoreAppFormGroup {
    #data?: ApiExamPlanInterface;

    constructor(data?: ApiExamPlanInterface) {
        super({
            [AddExamFormFields.Title]: new FormControl({value: data?.exam.title ?? ''}),
            [AddExamFormFields.Book]: new FormControl({value: data?.book.uuid ?? '', disabled: true}, Validators.required),
            [AddExamFormFields.UserIds]: new FormControl({value: data?.userIds ?? '', disabled: true}, Validators.required),
            [AddExamFormFields.Exam]: new FormControl({value: data?.exam.id ?? '', disabled: true}, Validators.required),
            [AddExamFormFields.StartDate]: new AppDateFormControl({state: data?.startDate, default: new Date()}, Validators.required),
            [AddExamFormFields.StartHour]: new AppDateFormControl({state: data?.startDate, default: new Date(), format: 'HH'}),
            [AddExamFormFields.StartMinute]: new AppDateFormControl({state: data?.startDate, default: new Date(), format: 'mm'}),
            [AddExamFormFields.EndDate]: new AppDateFormControl({state: data?.endDate}, Validators.required),
            [AddExamFormFields.EndHour]: new AppDateFormControl({state: data?.endDate, default: new Date(), format: 'HH'}),
            [AddExamFormFields.EndMinute]: new AppDateFormControl({state: data?.endDate, default: new Date(), format: 'mm'}),
        });

        this.#data = data;
    }

    get title(): FormControl {
        return this.get(AddExamFormFields.Title) as FormControl;
    }

    get userIds(): FormControl {
        return this.get(AddExamFormFields.UserIds) as FormControl;
    }

    get book(): FormControl {
        return this.get(AddExamFormFields.Book) as FormControl;
    }

    get exam(): FormControl {
        return this.get(AddExamFormFields.Exam) as FormControl;
    }

    get startDate(): FormControl {
        return this.get(AddExamFormFields.StartDate) as FormControl;
    }

    get startHour(): FormControl {
        return this.get(AddExamFormFields.StartHour) as FormControl;
    }

    get startMinute(): FormControl {
        return this.get(AddExamFormFields.StartMinute) as FormControl;
    }

    get endDate(): FormControl {
        return this.get(AddExamFormFields.EndDate) as FormControl;
    }

    get endHour(): FormControl {
        return this.get(AddExamFormFields.EndHour) as FormControl;
    }

    get endMinute(): FormControl {
        return this.get(AddExamFormFields.EndMinute) as FormControl;
    }

    setErrorByContext(context: HttpValidationErrorResponseContextInterface): void {
        const path: undefined | string = context.path;

        if (undefined === path) {
            return;
        }

        this.get(path)?.setErrors({backend: context.code});
    }

    clearField(field: AddExamFormFields): AppExamAssignDialogForm {
        this.get(field)?.setValue('');

        return this;
    }

    enableOrDisableField(field: AddExamFormFields, value: boolean): AppExamAssignDialogForm {
        if (value) {
            this.get(field)?.enable();
        } else {
            this.get(field)?.disable();
        }

        return this;
    }

    getMode(): HttpMethodsEnum.Put | HttpMethodsEnum.Post {
        return undefined === this.#data ? HttpMethodsEnum.Post : HttpMethodsEnum.Put;
    }

    setTitle(value?: string): AppExamAssignDialogForm {
        this.patchValue({[AddExamFormFields.Title]: value ?? ''});

        return this;
    }

    getAll(): ExamAssignInterface {
        const dateFormat: string = 'YYYY-MM-DD HH:mm:00';

        return {
            examId: this.exam.value,
            userIds: [...new Set(this.userIds.value.filter((userId: number | string) => '' !== userId))] as number[],
            startDate: moment(this.startDate.value).set({hour: this.startHour.value, minute: this.startMinute.value}).format(dateFormat),
            endDate: moment(this.endDate.value).set({hour: this.endHour.value, minute: this.endMinute.value}).format(dateFormat),
        }
    }
}
